import { useEffect, useMemo, useState } from "react";
import {
  hasAdminAccess,
  hasStaffAccess,
  hasMarketingAccess,
  isNewClient,
  intakeIsComplete,
  isLoggedIn,
} from "../../hooks/usePortalAuth";
import PracticeLogo from "./PracticeLogo";
import { apiGetPortalProfiles, type PortalProfileChoice } from "@/lib/api";

export default function PortalFooter() {
  const [, setRoleRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setRoleRevision((revision) => revision + 1);
    window.addEventListener("portalRolesChanged", refresh);
    window.addEventListener("previewRoleChanged", refresh);
    return () => {
      window.removeEventListener("portalRolesChanged", refresh);
      window.removeEventListener("previewRoleChanged", refresh);
    };
  }, []);
  const isAdminUser = hasAdminAccess();
  const isStaffUser = hasStaffAccess();
  const isMarketingUser = hasMarketingAccess();
  const isClientPortalUser = !isStaffUser && !isMarketingUser;
  const [activeProfile,setActiveProfile]=useState<PortalProfileChoice|null>(null);
  // A staff session does not require client portal access. A denied client
  // profile read would revoke the cached session while its chart is loading.
  useEffect(()=>{if(!isClientPortalUser||!isLoggedIn())return;let cancelled=false;void apiGetPortalProfiles().then(response=>{if(!cancelled&&response.data)setActiveProfile(response.data.active_profile);});return()=>{cancelled=true;};},[isClientPortalUser]);
  const capabilities=isClientPortalUser?activeProfile?.capabilities:undefined;
  const payerPortal=isClientPortalUser&&activeProfile?.relationship_type==="payer";
  const showIntake = useMemo(() => {
    if (isAdminUser) return false;
    return capabilities?.intake===true&&isNewClient() && !intakeIsComplete();
  }, [capabilities?.intake,isAdminUser]);

  const logoHref = useMemo(() => {
    if (isAdminUser) return "/admin";
    if (isStaffUser) return "/staff";
    if (isMarketingUser) return "/marketing";
    if (isLoggedIn()) {
      if (isNewClient() && !intakeIsComplete()) return "/clientportal/intake";
      return "/clientportal/dashboard";
    }
    return "/clientportal/dashboard";
  }, [isAdminUser,isStaffUser,isMarketingUser]);

  return (
    <footer className="w-full pt-14 pb-16 md:pb-12 bg-brand-warm">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 gap-10 mb-12 lg:grid-cols-3">
          <div>
            <a
              href={logoHref}
              className="inline-block mb-4 cursor-pointer"
            >
              <PracticeLogo showText={false} size="lg" />
            </a>
            <p className="font-manrope text-brand-dark/75 text-[15px] leading-relaxed max-w-xs">
              {payerPortal?"Review authorized billing, complete payer forms, and contact administrative support securely.":"Manage appointments, communicate with your therapist, access documents, and review billing — all in one secure place."}
            </p>
          </div>
          <div>
            <h5 className="font-manrope text-[13px] font-semibold tracking-widest uppercase mb-4">Portal</h5>
            <ul className="flex flex-col gap-2.5">
              {!isAdminUser && capabilities?.dashboard===true && !showIntake && (
                <li>
                  <a href="/clientportal/dashboard" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                    Dashboard
                  </a>
                </li>
              )}
              {!isAdminUser && capabilities?.intake===true && showIntake && (
                <li>
                  <a href="/clientportal/intake" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                    Intake
                  </a>
                </li>
              )}
              {!isAdminUser && capabilities && (
                <>
                  {capabilities.appointments===true&&<li>
                    <a href="/clientportal/appointments" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                      My Appointments
                    </a>
                  </li>}
                  {capabilities.messages===true&&<li>
                    <a href="/clientportal/messages" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                      Messages
                    </a>
                  </li>}
                  {!showIntake && capabilities.documents===true && (
                    <li>
                      <a href="/clientportal/documents" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                        Documents
                      </a>
                    </li>
                  )}
                  {capabilities.billing===true&&<li>
                    <a href="/clientportal/billing" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                      Billing
                    </a>
                  </li>}
                </>
              )}
              {isAdminUser && (
                <li>
                  <a href="/admin" className="font-manrope text-[15px] text-brand-dark/75 hover:text-brand-dark transition-colors cursor-pointer">
                    Admin
                  </a>
                </li>
              )}
            </ul>
          </div>
          <div>
            <h5 className="font-manrope text-[13px] font-semibold tracking-widest uppercase mb-4">Support</h5>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-2.5">
                <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-phone-line text-[#3C6FF8] text-sm" />
                </span>
                <a href="tel:+18389108150" className="font-manrope text-[15px] text-brand-dark/75 hover:text-[#3C6FF8] transition-colors cursor-pointer">
                  (838) 910-8150
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-[#4E7C59] text-sm" />
                </span>
                <a href="mailto:Info@perspectivetherapyNY.com" className="font-manrope text-[15px] text-brand-dark/75 hover:text-[#4E7C59] transition-colors cursor-pointer break-words">
                  Info@perspectivetherapyNY.com
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-time-line text-[#7C5CFA] text-sm" />
                </span>
                <span className="font-manrope text-[15px] text-brand-dark/75 leading-snug">
                  Mon–Fri: 9am – 9pm
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-brand-border pt-6">
          <p className="font-manrope text-[13px] text-brand-dark/60 text-center sm:text-left">
            © 2026 Perspective Therapy Mental Health Counseling, PLLC. All rights reserved. Private client portal.
          </p>
        </div>
      </div>
    </footer>
  );
}
